import type { ConnectMenuItem } from "@spiriidk/connect-configs";
import { useCallback, useEffect, useState } from "react";
import type { MouseEvent } from "react";
import { navigateToUrl } from "single-spa";

interface UseMenuNavigationOptions {
  onNavigate?: (href: string) => void;
}

function getCurrentPathname() {
  return window.location.pathname || "/";
}

function normalizePath(path: string) {
  if (!path || path === "/") {
    return "/";
  }

  const [pathname] = path.split(/[?#]/);
  if (!pathname || pathname === "/") {
    return "/";
  }

  let result = pathname;
  while (result.length > 1 && result.endsWith("/")) {
    result = result.slice(0, -1);
  }
  return result;
}

function pathsMatch(targetUrl: string, currentPathname: string): boolean {
  const normalizedTarget = normalizePath(targetUrl);
  const normalizedCurrent = normalizePath(currentPathname);

  if (normalizedCurrent === normalizedTarget) {
    return true;
  }

  if (normalizedTarget === "/") {
    return false;
  }

  return normalizedCurrent.startsWith(`${normalizedTarget}/`);
}

function matchesActiveWhen(
  activeWhen: ConnectMenuItem["activeWhen"],
  currentPathname: string
): boolean {
  if (!activeWhen) {
    return false;
  }

  if (typeof activeWhen === "function") {
    return activeWhen(currentPathname);
  }

  return activeWhen.some((matcher) => {
    if (typeof matcher === "string") {
      return pathsMatch(matcher, currentPathname);
    }
    if (matcher instanceof RegExp) {
      return matcher.test(currentPathname);
    }
    return false;
  });
}

function isMenuItemActive(
  item: ConnectMenuItem,
  currentPathname: string
): boolean {
  if (item.url && pathsMatch(item.url, currentPathname)) {
    return true;
  }

  return matchesActiveWhen(item.activeWhen, currentPathname);
}

function isExternalUrl(href: string): boolean {
  try {
    const url = new URL(href, window.location.origin);
    return url.origin !== window.location.origin;
  } catch {
    return false;
  }
}

function shouldNavigateClientSide(
  event: MouseEvent<HTMLAnchorElement>
): boolean {
  if (event.defaultPrevented) {
    return false;
  }

  if (typeof event.button === "number" && event.button !== 0) {
    return false;
  }

  if (event.metaKey || event.altKey || event.ctrlKey || event.shiftKey) {
    return false;
  }

  const target = event.currentTarget?.getAttribute("target");
  return !target || target === "_self";
}

function navigateToPath(href: string, onNavigate?: (href: string) => void) {
  if (onNavigate) {
    onNavigate(href);
    return;
  }

  navigateToUrl(href);
}

function useMenuNavigation({ onNavigate }: UseMenuNavigationOptions = {}) {
  const [currentPath, setCurrentPath] = useState(() =>
    normalizePath(getCurrentPathname())
  );

  useEffect(() => {
    const handlePopState = () => {
      setCurrentPath(normalizePath(getCurrentPathname()));
    };

    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, []);

  const handleNavigationInternal = useCallback(
    (event: MouseEvent<HTMLAnchorElement>) => {
      if (!shouldNavigateClientSide(event)) {
        return;
      }

      const href = event.currentTarget?.getAttribute("href");
      if (!href) {
        return;
      }

      event.preventDefault();

      if (isExternalUrl(href)) {
        window.open(href, "_blank", "noopener,noreferrer");
        return;
      }

      navigateToPath(href, onNavigate);
      setCurrentPath(normalizePath(href));
    },
    [onNavigate]
  );

  const handleNavigation = useCallback(
    (event: MouseEvent<HTMLAnchorElement>) => handleNavigationInternal(event),
    [handleNavigationInternal]
  );

  const isActive = useCallback(
    (item: ConnectMenuItem) => isMenuItemActive(item, currentPath),
    [currentPath]
  );

  return {
    handleNavigation,
    isActive,
    currentPath,
  };
}

export { useMenuNavigation };
